import { apiService } from "src/services";
import { AskKuralUserMode, AskKuralVersionType, IGenaiUsageResponse, QueriesConfigType, StudyType } from "../models/interfaces";
import { openForbiddenModal } from "src/pages/Studies/states/studiesSlice";
import { getBearerToken } from "src/services/axiosInstance";
import { AppDispatch } from "src/store";
import { ObjectId } from "bson";
import { IQuantFePayload } from "../models/interfaces";
import { buildAskKuralPPTExportPayload } from "../transformer";
import { IDownloadUrlData, IStandardApiResponse } from "src/pages/Studies/models/interfaces";
import { environment, isTruthy } from "src/config/environment";
import { IDeckPlanResponse } from "../models/pptInterfaces";
import { ASK_KURAL_PPT_TYPE, normalizeDeckResponse } from "../utils/pptDeck";

const isGatewayEnabled = isTruthy(environment.REACT_APP_GATEWAY_ENABLED);

const isAskKuralMtVersion = (askKuralVersion: AskKuralVersionType) =>
	askKuralVersion === AskKuralVersionType.v3 ||
	askKuralVersion === AskKuralVersionType.v3_1 ||
	askKuralVersion === AskKuralVersionType.v3_2 ||
	askKuralVersion === AskKuralVersionType.v3_3 ||
	askKuralVersion === AskKuralVersionType.v4;

const getStreamBaseUrl = () => {
	const base = environment.REACT_APP_API_URL;
	return isGatewayEnabled ? `${base}stream/` : `${base}`;
};

type HarnessFilterPayload = {
	entity_id: string[];
	file_id: string[];
	meta_id: string[];
	search_type: string;
};

type HarnessStartResponse = {
	data?: {
		message_id?: string;
	};
	error?: Record<string, unknown> | null;
	message?: string;
};

export const saveQuery = async (values: any) => {
	return await apiService.put("search/", values);
};

export const submitRating = async (values: any) => {
	return await apiService.put("search/feedback", values);
};

export const getSavedQueries = async (chat_type: string) => {
	return await apiService.get("search/queries", { chat_type });
};

export const getGenaiUsage = async (): Promise<IGenaiUsageResponse> => {
	return await apiService.get("/masters/genai-usage");
};

export const getDropdownValues = async (chat_type: string) => {
	return await apiService.get("search/dropdown", { chat_type });
};

export const getReference = async (values: any) => {
	return await apiService.get<IStandardApiResponse<IDownloadUrlData | unknown[]>>("search/files", { ...values });
};

export const downloadReferenceFile = async (values: any) => {
	const { file_name, file_extension } = values;
	const fileExtension = file_extension || file_name?.split(".").pop();
	const fileNameWithoutExtension = file_name?.substring(0, file_name.lastIndexOf("."));
	const downloadFileName = `${fileNameWithoutExtension}.pdf`;

	try {
		const response = await apiService.get<IStandardApiResponse<IDownloadUrlData>>("search/files", { ...values });
		const downloadUrl = response.data?.url;
		if (!downloadUrl) {
			throw new Error("No download URL received");
		}
		const fileResponse = await fetch(downloadUrl);
		if (fileResponse.ok) {
			const blob = await fileResponse.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = downloadFileName;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		} else {
			throw new Error("Failed to download file");
		}
	} catch (err) {
		console.error("Error downloading file: ", err);
		throw err;
	}
};

export const getQueryResults = async (values: any) => {
	return await apiService.get("search/saved_query", values);
};

export const deleteSavedQuery = async (query_id: string) => {
	return await apiService.delete("search/", { query_id });
};

export const getAllChats = async (values: any) => {
	return await apiService.get("chat/", values);
};

export const getPaginatedChats = async (values: any) => {
	return await apiService.get("chat/paginated", values);
};

export const getChatResults = async (values: any) => {
	return await apiService.get("chat/detail", values);
};

export const deleteSavedChat = async (values) => {
	return await apiService.delete("chat/", values);
};

export const updateChat = async (values: any) => {
	return await apiService.put("chat/", values);
};

export const chatQueryFeedback = async (values: any) => {
	return await apiService.post("chat/query/feedback", values);
};

export const getPaginatedChatsNew = async (values: any) => {
	return await apiService.get("chat/m_chats/", values);
};

export const getChatResultsNew = async (values: any) => {
	return await apiService.get("chat/m_chat/detail", values);
};

export const getWidgetData = async (values: any) => {
	return await apiService.get("search/agentic_widget_reference", { ...values });
};

export const chatQueryFeedbackNew = async (values: any) => {
	return await apiService.post("chat/m_chats/query/feedback", values);
};

export const reflectiveModeFeedback = async (values: any) => {
	return await apiService.post("chat/reflective-mode-feedback", values);
};

type StopStreamingPayloadInput = {
	message_id: string;
	chat_id?: string | null;
	local_chat_id?: string | null;
};

export const buildStopStreamingPayload = (values: StopStreamingPayloadInput) => ({
	message_id: String(values.message_id),
	chat_id: String(values.chat_id || values.local_chat_id || ""),
});

export const stopStreaming = async (values: StopStreamingPayloadInput) => {
	return await apiService.post("search/stop-generation", buildStopStreamingPayload(values));
};

export const deleteSavedChatNew = async (values) => {
	return await apiService.delete("chat/m_chat/", values);
};

export const updateChatNew = async (values: any) => {
	return await apiService.put("chat/mt_chat/", values);
};

export const getStreamResponse = async (
	inputValue: string,
	searchType: string,
	studies: { [key: string]: string[] },
	datasets: { [key: string]: string[] },
	metaTags: { [key: string]: string[] },
	selectedTags: string[],
	userProfileId: string,
	chat_id: string,
	branch_id: string,
	message_id: string,
	chatType: string,
	askKuralVersion: AskKuralVersionType,
	userRole: string,
	allSearchType: string[],
	chat_language: string,
	abortController: AbortController,
	dispatch: AppDispatch,
	isCountWidgetQuery: boolean,
	countAllFilters: any,
	sourceWidget: any,
	widgets?: string[],
	labels?: string[],
) => {
	let payload: any;

	if (isAskKuralMtVersion(askKuralVersion)) {
		if (isCountWidgetQuery) {
			payload = {
				query: inputValue,
				user_id: "",
				filters: {
					entity_id: studies,
					file_id: datasets,
					meta_id: metaTags,
					search_type: searchType,
					all_search_type: allSearchType,
				},
				all_filters: {
					entity_id: countAllFilters.entity_id,
					file_id: countAllFilters.file_id,
					meta_id: countAllFilters.meta_id,
					search_type: countAllFilters.search_type,
				},
				message_id,
				chat_id,
				branch_id,
				chat_type: chatType,
				chat_language: chat_language,
				source_widget: sourceWidget,
			};
			// Only add labels if it exists and is not empty in case of count widget
			if (labels && labels.length > 0) {
				payload.labels = labels;
			}
		} else {
			payload = {
				query: inputValue,
				user_id: "",
				filters: {
					entity_id: studies[searchType],
					file_id: datasets[searchType],
					meta_id: metaTags[searchType],
					search_type: searchType,
					all_search_type: allSearchType,
				},
				all_filters: {
					entity_id: Object.entries(studies).flatMap(([studyType, ids]) => ids),
					file_id: Object.entries(datasets).flatMap(([studyType, ids]) => ids),
					meta_id: selectedTags,
					search_type: allSearchType,
				},
				message_id,
				chat_id,
				branch_id,
				chat_type: chatType,
				chat_language: chat_language,
			};
			// Only add widgets if it exists and is not empty
			if (widgets && widgets.length > 0) {
				payload.widgets = widgets;
			}
		}
	} else if (askKuralVersion === AskKuralVersionType.v2) {
		payload = {
			query: inputValue,
			user_id: "",
			filters: {
				entity_id: studies[searchType],
				file_id: datasets[searchType],
				meta_id: selectedTags,
				search_type: searchType,
			},
			chat_id,
			chat_type: chatType,
			chat_language: chat_language,
		};
	} else {
		payload = {
			query: inputValue,
			user_id: "",
			filters: {
				entity_id: studies[searchType],
				file_id: datasets[searchType],
				meta_id: selectedTags,
				search_type: searchType,
			},
			chat_type: chatType,
			chat_language: chat_language,
		};
	}

	const searchUrl =
		askKuralVersion === AskKuralVersionType.v1
			? `${getStreamBaseUrl()}search/query`
			: `${getStreamBaseUrl()}search/chat${isAskKuralMtVersion(askKuralVersion) ? "_mt" : ""}`;
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
		"profile-id": userProfileId,
		"role-id": userRole,
	};
	const token = await getBearerToken();
	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}

	const response = await fetch(searchUrl, {
		method: "POST",
		headers,
		credentials: "include",
		body: JSON.stringify({ ...payload, searchWord: inputValue }),
		signal: abortController?.signal,
	});

	if (response.status === 403) {
		const errorResponse = await response.json();
		dispatch(openForbiddenModal({ message: errorResponse?.detail?.message, type: errorResponse?.detail?.error_type }));
	}

	if (!response.ok) {
		throw new Error(`HTTP error! Status: ${response.status}`);
	}

	return response;
};

export const getQuantStreamResponse = async (
	inputValue: string,
	searchType: string,
	studies: { [key: string]: string[] },
	datasets: { [key: string]: string[] },
	metaTags: { [key: string]: string[] },
	selectedTags: string[],
	userProfileId: string,
	chat_id: string,
	branch_id: string,
	message_id: string,
	chatType: string,
	askKuralVersion: AskKuralVersionType,
	userRole: string,
	allSearchType: string[],
	chat_language: string,
	abortController: AbortController,
	dispatch: AppDispatch,
) => {
	const transformedStudies = studies[searchType]?.map((study: StudyType) => {
		return {
			entity_id: study,
			wave_ids: [],
		};
	});

	let payload: any;

	if (isAskKuralMtVersion(askKuralVersion)) {
		payload = {
			query: inputValue,
			user_id: "",
			filters: {
				entity_id: studies[searchType],
				file_id: datasets[searchType],
				meta_id: metaTags[searchType],
				search_type: searchType,
				all_search_type: allSearchType,
			},
			all_filters: {
				entity_id: Object.entries(studies).flatMap(([studyType, ids]) => ids),
				file_id: Object.entries(datasets).flatMap(([studyType, ids]) => ids),
				meta_id: selectedTags,
				search_type: allSearchType,
			},
			message_id,
			chat_id,
			branch_id,
			chat_type: chatType,
			chat_language: chat_language,
		};
	} else if (askKuralVersion === AskKuralVersionType.v2) {
		payload = {
			query: inputValue,
			user_id: "",
			filters: {
				entity_id: studies[searchType],
				file_id: datasets[searchType],
				meta_id: selectedTags,
				search_type: searchType,
				all_search_type: allSearchType,
			},
			chat_id,
			chat_type: chatType,
			chat_language: chat_language,
		};
	} else {
		payload = {
			query: inputValue,
			user_id: "",
			filters: {
				entity_id: studies[searchType],
				file_id: datasets[searchType],
				meta_id: selectedTags,
				search_type: searchType,
				all_search_type: allSearchType,
			},
			chat_type: chatType,
			chat_language: chat_language,
		};
	}

	const searchUrl = `${getStreamBaseUrl()}quant/chat${isAskKuralMtVersion(askKuralVersion) ? "_mt" : ""}`;

	const headers: Record<string, string> = {
		"Content-Type": "application/json",
		"profile-id": userProfileId,
		"role-id": userRole,
	};
	const token = await getBearerToken();
	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}

	const response = await fetch(searchUrl, {
		method: "POST",
		headers,
		credentials: "include",
		body: JSON.stringify({ ...payload, searchWord: inputValue }),
		signal: abortController?.signal,
	});

	if (response.status === 403) {
		const errorResponse = await response.json();
		dispatch(openForbiddenModal({ message: errorResponse?.detail?.message, type: errorResponse?.detail?.error_type }));
	}

	if (!response.ok) {
		throw new Error(`HTTP error! Status: ${response.status}`);
	}

	return response;
};

export const submitInterruptElements = async (values: any) => {
	const { userProfileId, userRole, payload } = values;
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
		"profile-id": userProfileId,
		"role-id": userRole,
	};
	const token = await getBearerToken();
	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}

	return await fetch(`${getStreamBaseUrl()}interrupt-response`, {
		method: "POST",
		headers,
		credentials: "include",
		body: JSON.stringify(payload),
	});
};

export const getStreamCountWidgetResponse = async (payload: any, userProfileId: string, userRole: string, abortController: AbortController, dispatch: AppDispatch) => {
	const countWidgetStreamUrl = `${getStreamBaseUrl()}search/counts-widget`;
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
		"profile-id": userProfileId,
		"role-id": userRole,
	};
	const token = await getBearerToken();
	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}

	const response = await fetch(countWidgetStreamUrl, {
		method: "POST",
		headers,
		credentials: "include",
		body: JSON.stringify({ ...payload }),
		signal: abortController?.signal,
	});

	if (response.status === 403) {
		const errorResponse = await response.json();
		dispatch(openForbiddenModal({ message: errorResponse?.detail?.message, type: errorResponse?.detail?.error_type }));
	}
	if (!response.ok) {
		throw new Error(`HTTP error! Status: ${response.status}`);
	}
	return response;
};

export const fetchCountWidgetLabelGenerator = async (values: any) => {
	return await apiService.post("search/widget/label-generator", values);
};

export const groupCustomSegmentLabels = async (values: Record<string, unknown>) => {
	return await apiService.post("search/widget/custom-segments/group-labels", values);
};

export const saveCustomSegments = async (values: Record<string, unknown>) => {
	return await apiService.post("search/widget/custom-segments/save", values);
};

export const downloadCountsQuotesWidgetData = async (values: any) => {
	try {
		const response: { url: string; file_name: string } = await apiService.post(`search/widget/export`, values?.payload);
		const fileResponse = await fetch(response.url);
		if (fileResponse.ok) {
			const blob = await fileResponse.blob(); // Convert the response to a blob
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = response.file_name; // Use the original file name for download
			document.body.appendChild(a); // Append the anchor to the body
			a.click(); // Trigger the download
			document.body.removeChild(a); // Remove the anchor from the body
			window.URL.revokeObjectURL(url); // Clean up the object URL
		} else {
			throw new Error("Failed to download file");
		}
	} catch (err) {
		console.error("Error downloading file: ", err);
		throw err;
	}
};

export const getAgenticAskKuralResponse = async (
	inputValue: string,
	searchType: string,
	studies: { [key: string]: string[] },
	datasets: { [key: string]: string[] },
	metaTags: { [key: string]: string[] },
	selectedTags: string[],
	userProfileId: string,
	chat_id: string,
	branch_id: string,
	message_id: string,
	query_id: string,
	chatType: string,
	askKuralVersion: AskKuralVersionType,
	userRole: string,
	allSearchType: string[],
	chat_language: string,
	mode: string,
) => {
	const payload = {
		query: inputValue,
		user_id: "",
		filters: {
			entity_id: studies[searchType],
			file_id: datasets[searchType],
			meta_id: metaTags[searchType],
			search_type: searchType,
			all_search_type: allSearchType,
		},
		all_filters: {
			entity_id: Object.entries(studies).flatMap(([studyType, ids]) => ids),
			file_id: Object.entries(datasets).flatMap(([studyType, ids]) => ids),
			meta_id: selectedTags,
			search_type: allSearchType,
		},
		message_id,
		query_id,
		chat_id,
		branch_id,
		chat_type: chatType,
		chat_language: chat_language,
		mode: mode,
	};

	const response = await apiService.post<HarnessStartResponse>("search/chat_mt_agentic", payload);
	return {
		data: response?.data,
		error: response?.error,
		message: response?.message,
		success: !response?.error && Boolean(response?.data?.message_id),
	};
	// return {
	// 	message: "Chat processed successfully",
	// 	success: true,
	// 	data: {
	// 		message_id: new ObjectId(),
	// 	},
	// 	error: null,
	// };
};

export const getHarnessAgenticAskKuralResponse = async (
	inputValue: string,
	studies: Record<string, string[]>,
	datasets: Record<string, string[]>,
	metaTags: Record<string, string[]>,
	selectedTags: string[],
	chat_id: string,
	branch_id: string,
	message_id: string,
	query_id: string,
	chatType: string,
	allSearchType: string[],
	chat_language: string,
	mode: string,
	userMode?: AskKuralUserMode,
) => {
	const harnessFilters = allSearchType.reduce((acc, searchType) => {
		acc[searchType] = {
			entity_id: studies[searchType] || [],
			file_id: datasets[searchType] || [],
			meta_id: metaTags[searchType] || [],
			search_type: searchType,
		};
		return acc;
	}, {} as Record<string, HarnessFilterPayload>);
	const selectedSearchTypes = allSearchType.filter(Boolean);

	const payload = {
		query: inputValue,
		user_id: "",
		filters: {
			entity_id: Object.entries(studies).flatMap(([, ids]) => ids),
			file_id: Object.entries(datasets).flatMap(([, ids]) => ids),
			meta_id: selectedTags,
			search_type: selectedSearchTypes.length > 1 ? StudyType.Auto : selectedSearchTypes[0] || StudyType.Auto,
			all_search_type: allSearchType,
		},
		all_filters: {
			entity_id: Object.entries(studies).flatMap(([, ids]) => ids),
			file_id: Object.entries(datasets).flatMap(([, ids]) => ids),
			meta_id: selectedTags,
			search_type: allSearchType,
		},
		harness_filters: harnessFilters,
		message_id,
		query_id,
		chat_id,
		branch_id,
		chat_type: chatType,
		chat_language,
		mode,
		...(userMode ? { user_mode: userMode } : {}),
	};

	const response = await apiService.post<HarnessStartResponse>("search/chat_mt_harness_agentic", payload);
	return {
		data: response?.data,
		error: response?.error,
		message: response?.message,
		success: !response?.error && Boolean(response?.data?.message_id),
	};
};

export const getStreamAgenticAskKuralResponse = async (
	chat_id: string,
	branch_id: string,
	query_id: string,
	message_id: string,
	userProfileId: string,
	userRole: string,
	abortController: AbortController,
	dispatch: AppDispatch,
	search_type?: string,
	mode?: string,
) => {
	const payload = {
		chat_id,
		branch_id,
		message_id: message_id,
		query_id,
		mode,
	};
	const agenticAskKuralStreamUrl = `${getStreamBaseUrl()}search/agentic_data`;
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
		"profile-id": userProfileId,
		"role-id": userRole,
	};
	const token = await getBearerToken();
	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}

	const response = await fetch(agenticAskKuralStreamUrl, {
		method: "POST",
		headers,
		credentials: "include",
		body: JSON.stringify({ ...payload }),
		signal: abortController?.signal,
	});

	if (response.status === 403) {
		const errorResponse = await response.json();
		dispatch(openForbiddenModal({ message: errorResponse?.detail?.message, type: errorResponse?.detail?.error_type }));
	}

	if (!response.ok) {
		throw new Error(`HTTP error! Status: ${response.status}`);
	}

	return response;
};

// PPT Export API functions
export const planDeck = async (payload: { message_id: string; chat_id: string; branch_id: string }): Promise<IDeckPlanResponse> => {
	const response = await apiService.post("ppt/", {
		ppt_type: ASK_KURAL_PPT_TYPE,
		...payload,
	});
	return normalizeDeckResponse(response);
};

export const refineDeck = async (payload: { deck_id: string; instructions: string }): Promise<IDeckPlanResponse> => {
	const response = await apiService.post(`ppt/${payload.deck_id}/refine`, {
		ppt_type: ASK_KURAL_PPT_TYPE,
		instructions: payload.instructions,
	});
	return normalizeDeckResponse(response);
};

export const generateDeck = async (payload: { deck_id: string }): Promise<IDeckPlanResponse> => {
	const response = await apiService.post<unknown, undefined>(`ppt/${payload.deck_id}/generate`, undefined);
	return normalizeDeckResponse(response);
};

export const getDeckStatus = async (deckId: string): Promise<IDeckPlanResponse> => {
	const response = await apiService.get(`ppt/${deckId}/status`);
	return normalizeDeckResponse(response);
};

export const deleteDeck = async (deckId: string): Promise<void> => {
	await apiService.delete(`ppt/${deckId}`);
};

export const downloadDeck = async (deckId: string, fileName?: string) => {
	try {
		const blob = await apiService.downloadFile(`ppt/${deckId}/download`);
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = fileName || "presentation.pptx";
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
	} catch (err) {
		console.error("Error downloading deck: ", err);
		throw err;
	}
};

/**
 * Downloads a single quant-citation chart as a PPT via /generate-chart-ppt
 * using the Ask Kural payload contract (source="ask-kural").
 *
 * Reads filename from the response's Content-Disposition header — backend
 * generates it. Falls back to a generic name only if the header is absent.
 *
 * Throws on HTTP error / abort / payload build failure so the caller
 * can surface a user-facing message.
 */
export const downloadQuantCitationPPT = async (fePayload: IQuantFePayload, userProfileId: string, userRole: string, dispatch: AppDispatch, studyName?: string): Promise<void> => {
	const payload = buildAskKuralPPTExportPayload(fePayload);
	if (!payload) {
		throw new Error("Invalid fe_payload — cannot build PPT export payload");
	}

	const url = `${getStreamBaseUrl()}quant/generate-chart-ppt`;
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
		"profile-id": userProfileId,
		"role-id": userRole,
	};
	const token = await getBearerToken();
	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}

	const response = await fetch(url, {
		method: "POST",
		headers,
		credentials: "include",
		body: JSON.stringify(payload),
	});

	if (response.status === 403) {
		const errorResponse = await response.json().catch(() => ({}));
		dispatch(openForbiddenModal({ message: errorResponse?.detail?.message, type: errorResponse?.detail?.error_type }));
		throw new Error("Forbidden");
	}

	if (!response.ok) {
		throw new Error(`HTTP error! Status: ${response.status}`);
	}

	// Fallback: extract filename from Content-Disposition header if the generated name is empty.
	const disposition = response.headers.get("Content-Disposition") || "";
	const utf8Match = disposition.match(/filename\*\s*=\s*[^']*''([^;]+)/i);
	const plainMatch = disposition.match(/filename\s*=\s*"?([^";]+)"?/i);
	const headerFileName = utf8Match?.[1] ?? plainMatch?.[1];

	// Primary: build filename as <StudyName_16chars>_<Question#>_<ChartTitle>_<MMM_DD>.pptx
	const sanitize = (s: string) =>
		s
			.replace(/[\\/:*?"<>|]+/g, "_")
			.replace(/\s+/g, "_")
			.trim();

	const buildGeneratedFilename = (): string => {
		const studyPart = sanitize((studyName || "Study").slice(0, 16));
		const questionPart = sanitize(fePayload?.question_number || "Q");
		const chartPart = sanitize(fePayload?.chart_title || "Chart");
		const now = new Date();
		const monthNames = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
		const datePart = `${monthNames[now.getMonth()]}_${String(now.getDate()).padStart(2, "0")}`;
		return `${studyPart}_${questionPart}_${chartPart}_${datePart}.pptx`;
	};

	const generatedFilename = buildGeneratedFilename();

	const fileName = generatedFilename || (headerFileName ? decodeURIComponent(headerFileName) : "chart-insights.pptx");

	const blob = await response.blob();
	const objectUrl = window.URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = objectUrl;
	a.download = fileName;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	window.URL.revokeObjectURL(objectUrl);
};
